import { create } from 'zustand';
import { Organization, OrganizationClient } from '../clients/OrganizationClient';
import { OrganizationStoreState } from '@models/OrganizationStoreState';

export const useOrganizationStore = create<OrganizationStoreState>((set) => ({
  organization: null,
  loading: false,
  error: null,
  isOrganizationOwner: false,
  isOrganizationMember: false,
  async getOrganization(organizationId: string, userId: string) {
    try {
      const organization = await OrganizationClient.getOrganization(
        organizationId
      );
      if (organization) {
        const isOrganizationOwner = organization.owner === userId;

        set({
          organization,
          isOrganizationOwner,
          isOrganizationMember: true,
          error: null,
        });
      } else {
        // User is not part of an organization yet
        set({
          organization: null,
          isOrganizationOwner: false,
          isOrganizationMember: false,
          error: null,
        });
      }
    } catch (error) {
      set({
        error:
          error instanceof Error ? error.message : 'Failed to fetch organization',
      });
      throw error;
    }
  },
  async createOrganization(organization: Omit<Organization, 'created_at'>) {
    try {
      set({ loading: true });
      const newOrganization = await OrganizationClient.createOrganization(
        organization
      );
      set({ organization: newOrganization });
      set({ loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
  async joinOrganization(joinCode: string, organizationId: string) {
    try {
      set({ loading: true });
      const updatedUser = await OrganizationClient.joinOrganization(
        joinCode,
        organizationId
      );
      set({ loading: false });
      return updatedUser;
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
  async exitOrganization(userId: string) {
    try {
      set({ loading: true });
      const updatedUser = await OrganizationClient.exitOrganization(userId);
      set({ organization: null });
      set({ loading: false });
      return updatedUser;
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
  async removeUserFromOrganization(userId: string) {
    try {
      set({ loading: true });
      await OrganizationClient.removeUserFromOrganization(userId);
      set({ loading: false });
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },
}));
